import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Sparkles, Heart, Brain, Sun, Layers, Users } from "lucide-react";

const ProgramSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const modules = [
    {
      icon: Heart,
      title: "Partie 1 : Développement Personnel",
      items: [
        { name: "Guérison des blessures", desc: "Libérer les blessures émotionnelles et énergétiques profondes" },
        { name: "Rencontre des différentes parts de soi", desc: "Reconnecter avec toutes les facettes de l'être" },
        { name: "Dépassement des masques et voiles", desc: "Se libérer des fausses identités" },
        { name: "Libération des conditionnements", desc: "Démanteler les schémas limitants" },
        { name: "Éveil spirituel et ascension", desc: "Guider vers une conscience élevée" },
      ],
    },
    {
      icon: Sparkles,
      title: "Partie 2 : Déploiement Spirituel",
      items: [
        { name: "Activation des potentiels", desc: "Éveiller les capacités latentes" },
        { name: "Déploiement des capacités", desc: "Développer les dons naturels et spirituels" },
        { name: "Cultiver les vertus", desc: "Croissance personnelle à travers les valeurs" },
        { name: "Qualité de guidance", desc: "Guider avec précision et bienveillance" },
        { name: "Conscience pure", desc: "Atteindre un état de clarté et paix intérieure" },
      ],
    },
    {
      icon: Brain,
      title: "Partie 3 : Supra Conscience",
      items: [
        { name: "Développement de la multi-conscience", desc: "Explorer les dimensions multiples" },
        { name: "Connexion aux doubles quantiques", desc: "Travailler avec les doubles pour une guérison accrue" },
        { name: "Travail avec le champ quantique", desc: "Utiliser les énergies quantiques fondamentales" },
      ],
    },
    {
      icon: Sun,
      title: "Partie 4 : Unité Créatrice",
      items: [
        { name: "Dimensions collectives de la conscience", desc: "Intégrer les dimensions universelles" },
        { name: "Synchronisation au Point Zéro", desc: "Atteindre l'état où tout est possible" },
        { name: "Conscience unitaire Source", desc: "Connexion à la source de transformation" },
      ],
    },
    {
      icon: Layers,
      title: "Partie 5 : Synthèse Systémique",
      items: [
        { name: "Exploration des différents plans", desc: "Naviguer à travers les plans de l'existence" },
        { name: "Transposition des informations", desc: "Applications pratiques dans votre pratique" },
        { name: "Présence équanime", desc: "Développer une présence calme et stable" },
      ],
    },
    {
      icon: Users,
      title: "Partie 6 : Cercles de Pratique",
      items: [
        { name: "Sessions pratiques", desc: "Intégrer les connaissances acquises" },
        { name: "Masterclass exclusives", desc: "Animées par Gilles Delieuze et experts renommés" },
        { name: "Mise en synergie", desc: "Combinaison des différents modules" },
      ],
    },
  ];

  return (
    <section
      ref={ref}
      id="program"
      data-testid="program-section"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #090920 0%, #030014 100%)" }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 block">
            Le Programme Complet
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ce que vous allez <span className="text-gradient-cosmic">apprendre</span>
          </h2>
          <p className="max-w-2xl mx-auto text-slate-400 text-lg">
            Un voyage en 6 parties et 24 modules pour maîtriser la thérapie quantique multidimensionnelle.
          </p>
        </motion.div>

        {/* Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {modules.map((module, index) => {
              const Icon = module.icon;
              return (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="glass-card border border-indigo-500/20 rounded-xl overflow-hidden data-[state=open]:border-cyan-500/30"
                >
                  <AccordionTrigger
                    data-testid={`accordion-trigger-${index}`}
                    className="px-6 py-5 hover:no-underline group"
                  >
                    <div className="flex items-center gap-4 text-left">
                      <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center group-data-[state=open]:bg-cyan-500/10 transition-colors">
                        <Icon className="w-6 h-6 text-purple-400 group-data-[state=open]:text-cyan-400 transition-colors" />
                      </div>
                      <span
                        className="text-xl sm:text-2xl font-light text-white"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {module.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="pl-16 space-y-4">
                      {module.items.map((item, itemIndex) => (
                        <motion.div
                          key={itemIndex}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: itemIndex * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
                          <div>
                            <h4 className="text-white font-medium">{item.name}</h4>
                            <p className="text-slate-400 text-sm">{item.desc}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </motion.div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4"
        >
          {[
            { value: "24", label: "Modules" },
            { value: "48H+", label: "Vidéo" },
            { value: "6", label: "Ebooks" },
            { value: "∞", label: "Accès illimité" },
          ].map((stat, index) => (
            <div
              key={index}
              className="glass-card p-4 rounded-xl text-center"
            >
              <div className="text-2xl font-light text-gradient-cosmic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {stat.value}
              </div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ProgramSection;
